/**
 * REST API Client
 * Axios wrapper with auth interceptors, token refresh, and Pydantic v2 extra='forbid' defense
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { stripUndefined, deepStripUndefined } from '../../utils';

/**
 * API Configuration
 */
export interface ApiConfig {
  baseURL: string;
  timeout: number;
  withCredentials: boolean;
}

/**
 * Default configuration from environment
 */
export function getApiConfig(): ApiConfig {
  // Use Vite env in browser, or fallback for Node/SSR
  const baseURL = (typeof import.meta !== 'undefined' ? (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_URL : undefined) || 'http://localhost:8000/api/v1';
  return {
    baseURL,
    timeout: 30000,
    withCredentials: true, // Required for httpOnly cookie refresh token
  };
}

/**
 * Create configured axios instance
 */
export function createApiClient(config?: Partial<ApiConfig>): AxiosInstance {
  const cfg = getApiConfig();
  const finalConfig = { ...cfg, ...config };

  const client = axios.create({
    baseURL: finalConfig.baseURL,
    timeout: finalConfig.timeout,
    withCredentials: finalConfig.withCredentials,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Request interceptor - inject auth token & strip undefined
  client.interceptors.request.use(
    (request: InternalAxiosRequestConfig) => {
      // Strip undefined from request body (Pydantic extra='forbid' defense)
      if (request.data && typeof request.data === 'object') {
        request.data = deepStripUndefined(request.data);
      }
      
      // Strip undefined from query params
      if (request.params && typeof request.params === 'object') {
        request.params = stripUndefined(request.params);
      }

      // Inject Authorization header from auth store (will be set by store)
      const accessToken = getStoredAccessToken();
      if (accessToken && !request.headers.Authorization) {
        request.headers.Authorization = `Bearer ${accessToken}`;
      }

      return request;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  // Response interceptor - handle 401 with token refresh
  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }> = [];

  const processQueue = (error: Error | null, token: string | null = null): void => {
    failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else if (token) {
        resolve(token);
      }
    });
    failedQueue = [];
  };

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Only handle 401 Unauthorized
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Prevent infinite retry loops
        if (isRefreshing) {
          // Queue the request until refresh completes
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return client(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Attempt token refresh using refresh token (sent via httpOnly cookie)
          const refreshResponse = await axios.post<{ access_token: string }>(
            `${finalConfig.baseURL}/auth/refresh`,
            {},
            { withCredentials: true }
          );

          const newAccessToken = refreshResponse.data.access_token;
          
          // Update auth store (will be imported dynamically to avoid circular deps)
          setStoredAccessToken(newAccessToken);
          
          // Update default header for future requests
          client.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          
          // Process queued requests
          processQueue(null, newAccessToken);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear auth and redirect to login
          processQueue(refreshError as Error, null);
          clearStoredAuth();
          window.location.href = '/auth?reason=session_expired';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Transform error response for consistent handling
      if (error.response) {
        const apiError: ApiError = {
          name: 'ApiError',
          status: error.response.status,
          message: (error.response.data as Record<string, unknown>)?.detail as string || error.message,
          errorCode: (error.response.data as Record<string, unknown>)?.error_code as string | undefined,
          fields: (error.response.data as Record<string, unknown>)?.fields as Record<string, string[]> | undefined,
        };
        return Promise.reject(apiError);
      }

      // Network or timeout error
      const networkError: ApiError = {
        name: 'NetworkError',
        status: 0,
        message: error.code === 'ECONNABORTED' ? 'Request timeout' : 'Network error',
        isNetworkError: true,
      };
      return Promise.reject(networkError);
    }
  );

  return client;
}

/**
 * Token storage - in-memory for access token (XSS defense)
 * Refresh token handled via httpOnly cookie by backend
 */
let memoryAccessToken: string | null = null;

export function getStoredAccessToken(): string | null {
  return memoryAccessToken;
}

export function setStoredAccessToken(token: string | null): void {
  memoryAccessToken = token;
}

export function clearStoredAuth(): void {
  memoryAccessToken = null;
}

/**
 * API Error type for consistent error handling
 */
export interface ApiError extends Error {
  name: string;
  status: number;
  message: string;
  errorCode?: string | undefined;
  fields?: Record<string, string[]> | undefined;
  isNetworkError?: boolean | undefined;
}

export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error
  );
}

/**
 * Default API client instance
 */
export const apiClient = createApiClient();

/**
 * Authenticated request helper - ensures token is attached
 */
export async function authenticatedRequest<T>(
  client: AxiosInstance,
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  const token = getStoredAccessToken();
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return client.request<T>(config);
}

/**
 * API Endpoints constants
 */
export const API_ENDPOINTS = {
  // Auth
  REGISTER: '/auth/register',
  LOGIN: '/auth/login',
  REFRESH: '/auth/refresh',
  
  // Replays
  REPLAY_BY_ID: (matchId: string) => `/replays/${matchId}`,
  REPLAY_HISTORY: '/replays/history',
  
  // Leaderboard
  LEADERBOARD: '/leaderboard',
  
  // Health
  LIVEZ: '/livez',
  READYZ: '/readyz',
  HEALTHZ: '/healthz',
} as const;

/**
 * Request/Response types for type-safe API calls
 */
export interface PaginatedRequest {
  limit?: number;
  offset?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeaderboardQueryParams {
  limit?: number;
}